import React, { useCallback, useMemo } from 'react';
import size from 'lodash/size';
import isEmpty from 'lodash/isEmpty';
import { useParams } from 'react-router-dom';

import { discontinuousTimeScaleProvider } from 'react-stockcharts/lib/scale';
import { last } from 'react-stockcharts/lib/utils';
import { IChartData, IChartDetails } from '../interface/chartImages.interface';
import { PbBody, PbCell, PbHead, PbRow, PbTable } from '../../../shared/components/table';
import { chartTimeRangeMapper } from '../../../shared/constants/api';
import { englishLocale, germanLocale } from './stockChart';
import { AskIcon, BidIcon, HighIcon, LowIcon, VolumeIcon } from '../../../shared/components/icons/icon';
import { customNumberFormatter } from '../../../shared/util/utility';

interface IProps {
	chartData: IChartData[];
	span: string;
	chartDetails: IChartDetails;
}

const ChartTableInfo: React.FC<IProps> = (props) => {
	const { chartData, span, chartDetails } = props;

	const params = useParams();

	const spanDropDown = useMemo(
		() => [
			{ label: `1 ${params.language === 'en' ? 'Day' : 'Tag'}`, value: '1d' },
			{ label: `1 ${params.language === 'en' ? 'Week' : 'Woche'}`, value: '1w' },
			{ label: `1 ${params.language === 'en' ? 'Month' : 'Monat'}`, value: '1m' },
			{ label: `3 ${params.language === 'en' ? 'Months' : 'Monate'}`, value: '3m' },
			{ label: `6 ${params.language === 'en' ? 'Months' : 'Monate'}`, value: '6m' },
			{ label: 'YTD', value: 'ytd' },
			{ label: `1 ${params.language === 'en' ? 'Year' : 'Jahr'}`, value: '1y' },
			{ label: `3 ${params.language === 'en' ? 'Year' : 'Jahr'}`, value: '3y' },
			{ label: `5 ${params.language === 'en' ? 'Year' : 'Jahr'}`, value: '5y' },
			{ label: `10 ${params.language === 'en' ? 'Year' : 'Jahr'}`, value: '10y' },
			{ label: 'Max', value: 'max' },
			{ label: `${params.language === 'en' ? 'Custom' : 'Benutzerdefiniert'}`, value: 'custom' }
		],
		[params.language]
	);

	const xScaleProvider = discontinuousTimeScaleProvider
		.inputDateAccessor((d: any) => {
			return new Date(d.date);
		})
		.setLocale(params.language == 'en' ? englishLocale : germanLocale);
	const { data, xAccessor } = xScaleProvider(chartData);

	const dataRange = useMemo(() => {
		const tempSpan = chartTimeRangeMapper(chartData)[span];
		const end = xAccessor(last(data));
		const start = xAccessor(data[Math.max(0, data.length - (tempSpan ? tempSpan : 365))]);
		return [start, end];
	}, [data, xAccessor, chartData, span]);

	const calculateHighAndLowValues = useCallback(
		(value: string) => {
			const dataSlice = chartData.slice(dataRange[0] + 1, dataRange[1] + 1).filter((item: IChartData) => {
				const chartItem = item[`cv${chartDetails.symbol}-${chartDetails.boerse}-0`];
				return typeof chartItem === 'object' && chartItem !== null && (chartItem as { type?: string }).type !== 'forecastedPrice';
			});
			const high = !isEmpty(dataSlice)
				? Math.max(
						...dataSlice.map((item: IChartData) => {
							const chartItem = item[`cv${chartDetails.symbol}-${chartDetails.boerse}-0`];
							return typeof chartItem === 'object' && chartItem !== null
								? (chartItem as { l?: number }).l ?? Number.NEGATIVE_INFINITY
								: Number.NEGATIVE_INFINITY;
						})
				  )
				: null;
			const low = !isEmpty(dataSlice)
				? Math.min(
						...dataSlice.map((item: IChartData) => {
							const chartItem = item[`cv${chartDetails.symbol}-${chartDetails.boerse}-0`];
							return typeof chartItem === 'object' && chartItem !== null
								? (chartItem as { l?: number }).l ?? Number.POSITIVE_INFINITY
								: Number.POSITIVE_INFINITY;
						})
				  )
				: null;
			if (value === 'high') {
				return customNumberFormatter(high, params.decimalSeparator ? params.decimalSeparator : '', 2);
			} else {
				return customNumberFormatter(low, params.decimalSeparator ? params.decimalSeparator : '', 2);
			}
		},
		[chartData, dataRange, chartDetails.symbol, chartDetails.boerse, params.decimalSeparator]
	);

	return (
		<div className='width-30 chart-information-table'>
			<PbTable className='table-main'>
				<PbHead className='information-header'>
					<PbRow>
						<PbCell className='table-head' colSpan={2} header>
							{params.language === 'en' ? 'Numbers' : 'Kursdaten'}
						</PbCell>
					</PbRow>
				</PbHead>
				<PbBody>
					<PbRow>
						<PbCell>
							<div>
								<span className='mr-10'>
									<BidIcon />
								</span>
								Bid (
								<span className='font-style--italic'>
									{span !== ''
										? spanDropDown.find((value) => value.value === span)?.label
										: `${params.language === 'en' ? 'custom.' : 'benutzerdef.'}`}
								</span>
								)
							</div>
						</PbCell>
						<PbCell>{chartData[size(chartData) - 1].dividend || '-'}</PbCell>
					</PbRow>
					<PbRow>
						<PbCell>
							<div>
								<span className='mr-10'>
									<AskIcon />
								</span>
								Ask (
								<span className='font-style--italic'>
									{span !== ''
										? spanDropDown.find((value) => value.value === span)?.label
										: `${params.language === 'en' ? 'custom.' : 'benutzerdef.'}`}
								</span>
								)
							</div>
						</PbCell>
						<PbCell>{chartData[size(chartData) - 1].absoluteChange || '-'}</PbCell>
					</PbRow>
					<PbRow>
						<PbCell>
							<div>
								<span className='mr-10'>
									<VolumeIcon />
								</span>
								Volume (
								<span className='font-style--italic'>
									{span !== ''
										? spanDropDown.find((value) => value.value === span)?.label
										: `${params.language === 'en' ? 'custom.' : 'benutzerdef.'}`}
								</span>
								)
							</div>
						</PbCell>
						<PbCell>-</PbCell>
					</PbRow>
					<PbRow>
						<PbCell>
							<div>
								<span className='mr-10'>
									<HighIcon />
								</span>
								{params.language === 'en' ? 'High' : 'Hoch'}(
								<span className='font-style--italic'>
									{span !== ''
										? spanDropDown.find((value) => value.value === span)?.label
										: `${params.language === 'en' ? 'custom.' : 'benutzerdef.'}`}
								</span>
								)
							</div>
						</PbCell>
						<PbCell>{calculateHighAndLowValues('high') || '-'}</PbCell>
					</PbRow>
					<PbRow>
						<PbCell>
							<div>
								<span className='mr-10'>
									<LowIcon />
								</span>
								{params.language === 'en' ? 'Low' : 'Tief'}(
								<span className='font-style--italic'>
									{span !== ''
										? spanDropDown.find((value) => value.value === span)?.label
										: `${params.language === 'en' ? 'custom.' : 'benutzerdef.'}`}
								</span>
								)
							</div>
						</PbCell>
						<PbCell>{calculateHighAndLowValues('low') || '-'}</PbCell>
					</PbRow>
				</PbBody>
			</PbTable>
		</div>
	);
};

export default ChartTableInfo;
